import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import axios from "../lib/http/axiosConfig";
import stomp from "../lib/realtime/stompClient";
import { ensureStompConnected } from "../lib/realtime/ensureStompConnected";
import { setSelectedCellIds } from "../features/RouteSlice.js";
import { useDemoScenario } from "../demo/DemoScenarioContext";
import {
  readDemoCellSelection,
  toggleDemoCell,
  writeDemoCellSelection,
} from "../demo/demoCellSelection";

// 실시간 연결이 끊겼을 때 다시 붙기까지 기다리는 시간.
const RECONNECT_DELAY_MS = 3000;

/**
 * 격자 셀 선택을 여러 PC 가 함께 보게 한다.
 *
 * 선택은 원래 브라우저 안에만 있어서 PC 마다 화면이 달랐다.
 * 이제 서버가 농장 단위로 들고 있고, 바뀌면 접속 중인 모든 화면으로 내려온다.
 *
 * 서버는 언제나 "바뀐 뒤의 전체 목록"을 보낸다. 받는 쪽에서 합치는 계산이 없으므로
 * 한 프레임을 놓쳐도 다음 프레임에서 저절로 맞춰진다.
 *
 * 연결은 직접 챙긴다. 드론 화면이 열어 주기를 기다리면 드론이 없는 농장에서 동작하지 않는다.
 */
export function useFarmCellSelection(farmId) {
  const dispatch = useDispatch();
  // 데모는 이 값을 서버에 두지 않는다. 실계정의 공유 선택을 덮어쓰고, STOMP 로
  // 같은 농장을 보는 다른 화면까지 바꿔 버리기 때문. 자세한 이유는 demo/demoCellSelection.js.
  const { isDemo } = useDemoScenario();
  // 내 클릭으로 생긴 응답이 방금 누른 값을 되돌리지 않도록, 마지막으로 보낸 순번을 들고 있는다.
  const seqRef = useRef(0);

  const apply = useCallback(
    (cellIds) => {
      if (!Array.isArray(cellIds)) return;
      dispatch(setSelectedCellIds(cellIds));
    },
    [dispatch]
  );

  // 서버의 현재 선택을 받아 그린다. 화면에 들어올 때와, 끊겼다 다시 붙었을 때 쓴다.
  const pull = useCallback(async () => {
    if (!farmId) return;
    if (isDemo) {
      apply(readDemoCellSelection(farmId));
      return;
    }
    try {
      const { data } = await axios.get(`/api/farms/${farmId}/cell-selection`);
      apply(data?.data?.cellIds ?? []);
    } catch (error) {
      console.warn("[CELLS] 선택 조회 실패", error);
    }
  }, [farmId, apply, isDemo]);

  useEffect(() => {
    pull();
  }, [pull]);

  // 다른 PC 의 변경을 실시간으로 받는다.
  useEffect(() => {
    if (!farmId) return undefined;
    // 데모는 공유할 상대가 없다. 구독하면 실계정의 변경이 데모 화면으로 흘러든다.
    if (isDemo) return undefined;

    let alive = true;
    let sub = null;
    let retryTimer = null;

    const subscribe = () => {
      if (!alive || sub) return;
      try {
        sub = stomp.subscribe(`/sub/farm-cells/${farmId}`, (frame) => {
          try {
            apply(JSON.parse(frame.body)?.cellIds ?? []);
          } catch (error) {
            console.warn("[CELLS] 수신 처리 실패", error);
          }
        });
        // 끊겨 있던 동안의 변경은 서버가 다시 보내 주지 않는다. 붙는 김에 현재 값을 맞춘다.
        pull();
      } catch (error) {
        console.warn("[CELLS] 구독 실패", error);
      }
    };

    const scheduleRetry = () => {
      if (!alive || retryTimer) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        connect();
      }, RECONNECT_DELAY_MS);
    };

    const connect = async () => {
      if (!alive) return;
      try {
        if (await ensureStompConnected()) subscribe();
        else scheduleRetry();
      } catch (error) {
        scheduleRetry();
      }
    };

    // 드론 화면이 드론을 바꾸며 연결을 끊으면 구독도 함께 사라진다. 그때 다시 붙는다.
    const offConnect = stomp.onConnect(() => {
      sub = null;
      subscribe();
    });
    const offClose = stomp.onClose(() => {
      sub = null;
      scheduleRetry();
    });

    if (stomp.isConnected()) subscribe();
    else connect();

    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
      offConnect?.();
      offClose?.();
      sub?.unsubscribe();
      sub = null;
    };
  }, [farmId, apply, pull, isDemo]);

  /** 셀 하나를 켜고 끈다. 화면은 즉시 바꾸고, 서버 응답이 오면 그 값으로 맞춘다. */
  const toggleCell = useCallback(
    async (cellId, selected, optimisticIds) => {
      if (!farmId) return;
      if (Array.isArray(optimisticIds)) dispatch(setSelectedCellIds(optimisticIds));
      if (isDemo) {
        apply(toggleDemoCell(farmId, cellId, selected));
        return;
      }
      const mySeq = ++seqRef.current;
      try {
        const { data } = await axios.patch(`/api/farms/${farmId}/cell-selection`, {
          cellId,
          selected,
        });
        // 더 최근 클릭이 이미 나갔으면 이 응답은 흘려보낸다. 안 그러면 화면이 한 번 되돌아간다.
        if (mySeq === seqRef.current) apply(data?.data?.cellIds ?? []);
      } catch (error) {
        console.warn("[CELLS] 선택 전송 실패", error);
      }
    },
    [farmId, dispatch, apply, isDemo]
  );

  /** 전체 선택이나 전체 해제처럼 한 번에 바꾼다. */
  const replaceCells = useCallback(
    async (cellIds) => {
      if (!farmId) return;
      dispatch(setSelectedCellIds(cellIds));
      if (isDemo) {
        apply(writeDemoCellSelection(farmId, cellIds));
        return;
      }
      const mySeq = ++seqRef.current;
      try {
        const { data } = await axios.put(`/api/farms/${farmId}/cell-selection`, { cellIds });
        if (mySeq === seqRef.current) apply(data?.data?.cellIds ?? []);
      } catch (error) {
        console.warn("[CELLS] 선택 전송 실패", error);
      }
    },
    [farmId, dispatch, apply, isDemo]
  );

  return { toggleCell, replaceCells };
}

export default useFarmCellSelection;
